import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators, FormControl } from '@angular/forms';
import { DropDownOption } from 'app/modules/common/entities/dropdown/dropDown-options';
import { DropDownModel } from 'app/modules/common/entities/DropDownModel';
import { AvisoBaja, SinTelefono, Tareas, TipoDeImportacion } from 'app/modules/common/enums/leave-import.enums';
import { FileService } from 'app/modules/common/services/file.service';
import { SharedService } from 'app/modules/common/services/shared-service';
import _ from 'lodash';
import { LeaveImportRulesService } from '../services/leave-import-rules.service';
import { FileFormatEnum } from 'app/modules/common/enums/file-format-enum';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

// drop down types used in this component
type DropDownTypes = "TipoDeImportacion" | "AvisoDeBaja" | "Tareas" | "CriteriosDeTareas" | "SinTelefono";

// all drop down options
const _dropDowns = {
    TipoDeImportacion: [
        { value: TipoDeImportacion.BajasAltas, label: "Bajas y altas" },
        { value: TipoDeImportacion.SoloAltas, label: "Sólo altas" },
        { value: TipoDeImportacion.SoloBajas, label: "Sólo bajas y altas con ref. trab." },
        { value: TipoDeImportacion.SoloDatos, label: "Sólo datos de baja" },
        { value: TipoDeImportacion.SoloTrabajadores, label: "Sólo trabajadores" },
        { value: TipoDeImportacion.SoloTrabajadoresPlantilla, label: "Sólo trabajadores (plantilla completa)" },
        { value: TipoDeImportacion.SoloTrabajadoresCon, label: "Sólo trabajadores con ref. trab." },
        { value: TipoDeImportacion.ImpQuironPrevencion, label: "Importación Quirón Prevención" },
    ],
    AvisoDeBaja: [
        { value: AvisoBaja.NoneSelected, label: "-Ninguna seleccionada-" },
        { value: AvisoBaja.BajasNuevas, label: "Bajas nuevas y avisos de altas de bajas activas" },
        { value: AvisoBaja.AvisosDeBaja, label: "Avisos de baja y de alta para todas las bajas" },
        { value: AvisoBaja.NoIncluirAvisos, label: "No incluir avisos" },
    ],
    Tareas: [
        { value: Tareas.NoneSelected, label: "-Ninguna seleccionada-" },
        { value: Tareas.ContactoAdministrativo, label: "Contacto administrativo" },
        { value: Tareas.PrimerContacto, label: "Primer contacto (SME)" },
        { value: Tareas.PrimerContactoTelefonico, label: "Primer contacto telefónico" },
        { value: Tareas.PrimeraVisita, label: "Primera visita" },
        { value: Tareas.SegunCriterios, label: "Según criterios" },
        { value: Tareas.SegunEmpresa, label: "Según empresa" },
        { value: Tareas.SinTarea, label: "Sin tarea" },
    ],
    SinTelefono: [
        { value: SinTelefono.NoneSelected, label: "-Ninguna seleccionada-" },
        { value: SinTelefono.ComprobarDireccion, label: "Comprobar dirección (Médico/SME)" },
        { value: SinTelefono.DejaLaTarea, label: "Deja la tarea de comprobar dirección" },
        { value: SinTelefono.Nada, label: "Nada" },
        { value: SinTelefono.NoSeImporta, label: "No se importa y genera un error" },
        { value: SinTelefono.TelefonoIncorrecto, label: "Teléfono incorrecto + Comprobar dirección" },
    ],
    CriteriosDeTareas: []
};


@Component({
    selector: 'app-leave-import-parameter',
    templateUrl: './leave-import-parameter.component.html',
    styleUrls: ['./leave-import-parameter.component.scss'],
    standalone: false
})
export class LeaveImportParameterComponent implements OnInit {

    // inputs
    @Input("parameters-form") parametersForm: UntypedFormGroup;
    @Input("templates-list") templatesList: DropDownOption[] = [];

    // events
    @Output("template-save-click") onTemplateSaveClick = new EventEmitter();
    @Output("template-delete-click") onTemplateDeleteClick = new EventEmitter();

    // drop down boxes
    dropDowns: Record<DropDownTypes, DropDownOption[] | DropDownModel[]> = _dropDowns;

    // store type here to use in view
    TareasEnum = Tareas;

    // forms array for tipo de bajas table    
    @Input("tipos-de-baha-fromsarray") tipoDeBajasFromArray: UntypedFormArray;
    tipoDeBajaTableColumns = ['tipoBaja', 'excel', 'options'];


    /**
     * Selected file in base 64 format
     */
    @Input() selectedFile = null;
    @Output() selectedFileChange = new EventEmitter<string>();

    /**
     * Name of the selected file to display on view
     */
    public selectedFileName: string = null;
    public FileFormatEnum = FileFormatEnum;

    constructor(
        private rulesService: LeaveImportRulesService,
        private fileService: FileService,
        private sharedService: SharedService,
        private fb: UntypedFormBuilder,
    ) { }

    ngOnInit(): void {
        this.listenCriteriaListChanges();
    }


    /**
     * Tarea selection change handler
     */
    public onTareaChange() {
        // set criterias as null if tarea is not segun criteria
        if (this.parametersForm.controls.Tareas.value !== Tareas.SegunCriterios)
            this.parametersForm.controls.Criterios.setValue(null);
    }


    //#region TASK CRITERIA

    /**
     * Listen for changes in criterias
     */
    private listenCriteriaListChanges() {
        this.rulesService.criterias
            .subscribe(res => {

                this.loadCriterios(res);

            }, err => {
                this.sharedService.ShowSnackBar("Error al cargar los criteras de las tareas");
                console.error(err);
            });
    }


    /**
     * Load task criterios into drop down
     */
    private loadCriterios(taskCriteras: DropDownModel[]) {
        this.dropDowns.CriteriosDeTareas = taskCriteras.map(t => ({
            label: t.viewValue, value: t.value
        }));
    }

    //#endregion


    //#region TIPO DE BAJA TABLE

    /**
     * Add row to the table
     */
    public onTipoBajaAdd() {
        // create new form
        const newForm = this.fb.group({
            tipoBaja: ["", [Validators.required]],
            excel: ["", [Validators.required]],
        });
        this.tipoDeBajasFromArray.push(newForm);

        // reassign to refresh table
        this.tipoDeBajasFromArray.controls = this.tipoDeBajasFromArray.controls.slice();
    }


    /**
     * Remove a row from tipo de baja table
     * @param row Tipo de baja table row
     */
    public onTipoDeBajaDelete(row: UntypedFormGroup) {
        // remove form
        const index = this.tipoDeBajasFromArray.controls.indexOf(row);
        this.tipoDeBajasFromArray.removeAt(index);

        // reassign to refresh table
        this.tipoDeBajasFromArray.controls = this.tipoDeBajasFromArray.controls.slice();
    }

    //#endregion


    //#region FILE

    /**
     * Handle EXCEL file picker changes
     */
    onFileChange(files: FileList) {
        // display selected file name
        if (files.length) {

            if (files.length > 1)
                this.sharedService.ShowSnackBar("No se permiten los archivos múltiples. Sólo se selecciona el primer archivo");

            //if (this.fileService.getFileExtention(files[0].type) === "XLSX") {
            if (FileFormatEnum.LeaveImportControllerTS.includes(this.fileService.getFileExtention(files[0].type))) {

                // read file
                this.selectedFileName = files[0].name;
                this.readFile(files[0]);

            } else {
                this.sharedService.ShowSnackBar("Sólo se permiten archivos de Excel");
            }

        }
    }


    // read given file for uploading
    private readFile(file: File) {

        let currentContext = this;

        let fileReader = new FileReader();
        fileReader.onload = function () {
            currentContext.selectedFile = currentContext.fileService.arrayBufferToBase64(fileReader.result);
            currentContext.selectedFileChange.emit(currentContext.selectedFile);
        };

        fileReader.readAsArrayBuffer(file);

    }

    //#endregion

}